import { IMAGES } from "@/constants/images";

export const VIDEO_SECTION = {
  eyebrow: "See it in action",
  title: "Precision workholding, in motion",
  subtitle:
    "Watch our principals' clamping systems perform on real shop-floor setups — from zero-point pallet changes to magnetic milling and 5-axis fixturing.",
};

// Each item: type 'youtube' (set `url` to any YouTube link) or 'file' (set `url` to a hosted mp4/webm).
// `poster` is the thumbnail shown before play (used for file type and as YouTube fallback).
export const VIDEOS = [
  {
    id: "v1",
    type: "youtube",
    url: "https://www.youtube.com/watch?v=Oh2xbQ02IWI",
    title: "Magnetic Workholding for CNC Milling",
    caption: "High-precision milling on a metal casting, held completely secure by magnetic chucks.",
    description:
      "Magnetic workholding eliminates traditional mechanical clamps — delivering 5-sided tool accessibility, uniform clamping force and dramatically reduced setup times on high-precision CNC milling.",
    features: [
      "Solid grip on irregular casting surfaces",
      "Zero vibration during heavy cuts",
      "Fast loading & unloading for higher productivity",
    ],
    poster: IMAGES.brands.rudra,
  },
  {
    id: "v2",
    type: "youtube",
    url: "https://www.youtube.com/watch?v=oSPkMUiYiTE",
    title: "EROWA — Precision Tooling Meets Automation",
    caption: "Where Swiss finesse meets function — the core of modern manufacturing.",
    description:
      "From Swiss precision tooling to fully automated robotic systems, EROWA creates harmonized frameworks where human expertise and smart automation work in perfect synergy — driving efficiency, safety and 24/7 autonomous production.",
    features: [
      "Standardized zero-point tooling framework",
      "Robotic loading for lights-out production",
      "Efficiency, safety and round-the-clock autonomy",
    ],
    poster: IMAGES.brands.erowa,
  },
  {
    id: "v3",
    type: "youtube",
    url: "https://www.youtube.com/watch?v=6QQ48w1Qydo",
    title: "SPREITZER ALUMESS.easyloc Metrology Fixturing",
    caption: "Modular, repeatable fixturing that streamlines CMM & optical inspection.",
    description:
      "ALUMESS.easyloc is a modular metrology fixturing system for tactile and optical measurement — offering flexible, quick and repeatable clamping of complex workpieces during inspection.",
    features: [
      "Fast, tool-free modular positioning & assembly",
      "High repeatability and precision holding",
      "Built for CMMs, measuring microscopes & optical systems",
    ],
    poster: IMAGES.brands.spreitzer,
  },
  {
    id: "v4",
    type: "youtube",
    url: "https://www.youtube.com/shorts/fIv-aobNonQ",
    title: "Beyond Six Sigma — Manufacturing Excellence",
    caption: "Better-than-6-Sigma defect rates with 50% higher efficiency.",
    description:
      "Through targeted process optimization, enhanced workholding and rigorous quality control, Teknik achieves a defect rate beyond the Six Sigma standard (< 3.4 defects per million) while boosting production efficiency by 50% — proof that world-class quality and high-volume efficiency go hand in hand.",
    features: [
      "Near-zero rejection — surpassing Six Sigma metrics",
      "50% cycle-time reduction / efficiency gain",
      "Higher throughput without compromising precision",
    ],
    poster: IMAGES.hero,
  },
  {
    id: "v5",
    type: "youtube",
    url: "https://www.youtube.com/watch?v=vwkAA0yoAM0",
    title: "EROWA PalletSet W — Wire EDM Workholding",
    caption: "Modular Wire-EDM clamping with 2-micron system accuracy.",
    description:
      "PalletSet W streamlines Wire EDM setup with seamless single-part positioning at 2-micron accuracy. Prepare workpieces off-machine at a presetting station to minimize spindle downtime and maximize productive hours.",
    features: [
      "3,000 N clamping power — manual chuck on standard bases",
      "Versatile: 3D pendulum, beam vices, prism & Univice holders",
      "Collision-free: wire-threading recesses & adjustable zero-level",
    ],
    poster: IMAGES.about1,
  },
  {
    id: "v6",
    type: "youtube",
    url: "https://www.youtube.com/watch?v=Lim6kYVfkTA",
    title: "SPREITZER Freeze Clamping — Zero Deformation",
    caption: "Frozen-film clamping for delicate, thin-walled & micro parts.",
    description:
      "Freeze clamping uses a frozen liquid film to hold components securely during machining — giving uniform, full-surface support without the warping that mechanical jaws cause on fragile, thin-walled or micro-sized parts.",
    features: [
      "Zero deformation on fragile, thin or delicate materials",
      "Holds micro-components impossible for standard jaws",
      "Uniform support — eliminates vibration & localized stress",
    ],
    poster: IMAGES.brands.spreitzer,
  },
  {
    id: "v7",
    type: "youtube",
    url: "https://www.youtube.com/watch?v=UqBQlmnb8fI",
    title: "Small Footprint, Massive Holding Power",
    caption: "A compact MAGNASLOT chuck anchors an oversized, overhanging steel block.",
    description:
      "A compact 300×400 mm MAGNASLOT chuck firmly anchors an oversized 500×800 mm steel block overhanging on all sides. High-density electro-permanent flux pulls the component down with uniform force, so a face mill can shred a 2 mm depth of cut at 500 mm/min with zero chatter or flex — proof that total-surface magnetic control outperforms bulky mechanical clamps.",
    features: [
      "Compact base, max capability — secures large parts overhanging the chuck without losing rigidity",
      "Unobstructed 5-side machining — no side clamps or vices, 100% collision-free access",
      "Vibration dampening — uniform contact absorbs heavy cuts, extending insert life",
      "Fail-safe & energy-efficient — a split-second pulse holds without power draw or outage risk",
    ],
    poster: IMAGES.brands.rudra,
  },
];

export function youtubeId(urlOrId) {
  if (!urlOrId) return "";
  if (!/[./]/.test(urlOrId)) return urlOrId; // already a bare ID
  const m = urlOrId.match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : "";
}
